import { useMemo } from 'react'
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import isNil from 'lodash/isNil'
import { extractError, isRequestRejected } from './extract-error'
import {
  bulkGrantAccess,
  getAllUsers,
  getCustomerOptions,
  getInviteRoles,
  getUserHistory,
  getUsers,
  removeFundingAccess,
  revokeCompanyAccess,
} from './user-invitation.api'
import {
  BulkGrantAccessRequestDto,
  BulkGrantAccessResponseDto,
  BulkGrantAction,
  CustomerOption,
  GetAllUsersResponseDto,
  GetUsersRequestDto,
  GetUsersResponseDto,
  InviteRole,
  RemoveFundingAccessAction,
  RemoveFundingAccessRequestDto,
  RemoveFundingAccessResponseDto,
  RevokeCompanyAccessAction,
  RevokeCompanyAccessRequestDto,
  RevokeCompanyAccessResponseDto,
  UserInvitationHistoryResponseDto,
} from './dto/user-invitation.dto'

export const QUERY_KEY = 'platform-users'
const CUSTOMERS_QUERY_KEY = 'platform-user-customers'
const HISTORY_QUERY_KEY = 'platform-user-history'
const INVITE_ROLES_QUERY_KEY = 'platform-user-invite-roles'

export const useRosterQuery = (
  params: GetUsersRequestDto = {},
  options?: Omit<
    UseQueryOptions<GetUsersResponseDto, Error, GetUsersResponseDto>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<GetUsersResponseDto, Error, GetUsersResponseDto>({
    queryKey: [QUERY_KEY, params],
    queryFn: () => getUsers(params),
    // Reuse the cached page on remount; mutations still invalidate + refetch.
    staleTime: 60 * 1000,
    ...options,
  })
}

/** The server rejects a longer list of addresses than this in one request. */
const EMAIL_BATCH = 100

/**
 * The rows held for a known set of addresses, for asking about a handful rather
 * than browsing. Keyed under the users key so a grant or revoke invalidates it
 * with the list, and skipped entirely when there is nothing to ask about.
 *
 * Longer lists are asked for in batches, because clearing a profile of a couple
 * of hundred recipients still has to be answered.
 */
export const useRosterByEmailQuery = (
  emails: string[],
  options?: Omit<
    UseQueryOptions<GetAllUsersResponseDto, Error, GetAllUsersResponseDto>,
    'queryKey' | 'queryFn'
  >
) => {
  const wanted = useMemo(() => {
    const normalized = emails.map((email) => email.trim().toLowerCase())
    return normalized
      .filter((email, index) => normalized.indexOf(email) === index)
      .sort()
  }, [emails])

  return useQuery<GetAllUsersResponseDto, Error, GetAllUsersResponseDto>({
    queryKey: [QUERY_KEY, 'by-email', wanted],
    queryFn: async () => {
      const batches: string[][] = []
      for (let at = 0; at < wanted.length; at += EMAIL_BATCH) {
        batches.push(wanted.slice(at, at + EMAIL_BATCH))
      }
      const answers = await Promise.all(
        batches.map((batch) => getAllUsers({ emails: batch }))
      )
      return {
        data: answers.flatMap((answer) => answer.data),
        total: answers.reduce((sum, answer) => sum + answer.total, 0),
      }
    },
    enabled: wanted.length > 0,
    staleTime: 30 * 1000,
    ...options,
  })
}

export const useCustomerOptionsQuery = (
  options?: Omit<
    UseQueryOptions<CustomerOption[], Error, CustomerOption[]>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<CustomerOption[], Error, CustomerOption[]>({
    queryKey: [CUSTOMERS_QUERY_KEY],
    queryFn: getCustomerOptions,
    staleTime: 5 * 60 * 1000,
    ...options,
  })
}

export const useUserHistoryQuery = (
  userId: number | null,
  options?: Omit<
    UseQueryOptions<
      UserInvitationHistoryResponseDto,
      Error,
      UserInvitationHistoryResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    UserInvitationHistoryResponseDto,
    Error,
    UserInvitationHistoryResponseDto
  >({
    queryKey: [HISTORY_QUERY_KEY, userId],
    queryFn: () => getUserHistory(userId as number),
    enabled: !isNil(userId),
    ...options,
  })
}

export const useInviteRolesQuery = (
  options?: Omit<
    UseQueryOptions<InviteRole[], Error, InviteRole[]>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<InviteRole[], Error, InviteRole[]>({
    queryKey: [INVITE_ROLES_QUERY_KEY],
    queryFn: getInviteRoles,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  })
}

/**
 * Each recipient is processed sequentially on the server and can involve Cognito
 * lookups, person writes, and an email send, so a full batch would sit well past
 * the gateway's idle timeout as one request. Splitting it keeps every call short,
 * lets the results list fill in as it goes, and means a batch larger than the
 * server's per-request cap needs no special handling here.
 */
export const GRANT_ACCESS_CHUNK_SIZE = 20
export const REVOKE_ACCESS_CHUNK_SIZE = 20

interface ChunkedRunConfig<TItem, TResponse> {
  items: TItem[]
  chunkSize: number
  send: (batch: TItem[]) => Promise<TResponse>
  /** Stands in for a chunk the server never answered. */
  deadChunk: (batch: TItem[], reason: string) => TResponse
  merge: (chunks: TResponse[]) => TResponse
  onProgress?: (processed: number, total: number) => void
}

const runInChunks = async <TItem, TResponse>({
  items,
  chunkSize,
  send,
  deadChunk,
  merge,
  onProgress,
}: ChunkedRunConfig<TItem, TResponse>): Promise<TResponse> => {
  const chunks: TResponse[] = []

  for (let i = 0; i < items.length; i += chunkSize) {
    const batch = items.slice(i, i + chunkSize)
    try {
      chunks.push(await send(batch))
    } catch (error) {
      // A failed chunk must not take the run down or vanish, wherever it falls:
      // its recipients are reported as failed so they stay visible and
      // retryable, and earlier chunks keep the results they already earned.
      const reason = extractError(error, 'Request failed')
      chunks.push(deadChunk(batch, reason))

      // A payload the server rejects outright (a role it will not act on, a
      // malformed id) fails the same way for every chunk, so stop sending and
      // report the rest against that reason rather than asking N more times.
      if (isRequestRejected(error)) {
        const untried = items.slice(i + batch.length)
        if (untried.length > 0) chunks.push(deadChunk(untried, reason))
        onProgress?.(items.length, items.length)
        break
      }
    }
    onProgress?.(Math.min(i + batch.length, items.length), items.length)
  }

  return merge(chunks)
}

interface BulkAccessMutationOptions<TResponse, TRequest>
  extends UseMutationOptions<TResponse, Error, TRequest> {
  /** Reports completed recipients so the modal can show progress mid-flight. */
  onProgress?: (processed: number, total: number) => void
}

const EMPTY_GRANT_RESPONSE: BulkGrantAccessResponseDto = {
  results: [],
  invitedCount: 0,
  resentCount: 0,
  roleAssignedCount: 0,
  skippedCount: 0,
  failureCount: 0,
}

const mergeGrantResponses = (
  chunks: BulkGrantAccessResponseDto[]
): BulkGrantAccessResponseDto =>
  chunks.reduce(
    (merged, chunk) => ({
      results: [...merged.results, ...chunk.results],
      invitedCount: merged.invitedCount + chunk.invitedCount,
      resentCount: merged.resentCount + chunk.resentCount,
      roleAssignedCount: merged.roleAssignedCount + chunk.roleAssignedCount,
      skippedCount: merged.skippedCount + chunk.skippedCount,
      failureCount: merged.failureCount + chunk.failureCount,
    }),
    EMPTY_GRANT_RESPONSE
  )

export const useBulkGrantAccessMutation = (
  options?: BulkAccessMutationOptions<
    BulkGrantAccessResponseDto,
    BulkGrantAccessRequestDto
  >
) => {
  const queryClient = useQueryClient()
  const { onProgress, ...mutationOptions } = options ?? {}

  return useMutation<
    BulkGrantAccessResponseDto,
    Error,
    BulkGrantAccessRequestDto
  >({
    ...mutationOptions,
    mutationFn: ({ grants }) =>
      runInChunks({
        items: grants,
        chunkSize: GRANT_ACCESS_CHUNK_SIZE,
        onProgress,
        send: (batch) => bulkGrantAccess({ grants: batch }),
        merge: mergeGrantResponses,
        deadChunk: (batch, reason) => ({
          ...EMPTY_GRANT_RESPONSE,
          results: batch.map((item) => ({
            email: item.email,
            action: BulkGrantAction.Failed,
            success: false,
            reason,
          })),
          failureCount: batch.length,
        }),
      }),
    onSuccess: (data, variables, context) => {
      // Invalidate once at the end rather than per chunk, so the table under
      // the open modal repaints a single time.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      mutationOptions.onSuccess?.(data, variables, context)
    },
  })
}

const EMPTY_REMOVE_FUNDING_RESPONSE: RemoveFundingAccessResponseDto = {
  results: [],
  removedCount: 0,
  skippedCount: 0,
  failureCount: 0,
}

const mergeRemoveFundingResponses = (
  chunks: RemoveFundingAccessResponseDto[]
): RemoveFundingAccessResponseDto =>
  chunks.reduce(
    (merged, chunk) => ({
      results: [...merged.results, ...chunk.results],
      removedCount: merged.removedCount + chunk.removedCount,
      skippedCount: merged.skippedCount + chunk.skippedCount,
      failureCount: merged.failureCount + chunk.failureCount,
    }),
    EMPTY_REMOVE_FUNDING_RESPONSE
  )

export const useRemoveFundingAccessMutation = (
  options?: BulkAccessMutationOptions<
    RemoveFundingAccessResponseDto,
    RemoveFundingAccessRequestDto
  >
) => {
  const queryClient = useQueryClient()
  const { onProgress, ...mutationOptions } = options ?? {}

  return useMutation<
    RemoveFundingAccessResponseDto,
    Error,
    RemoveFundingAccessRequestDto
  >({
    ...mutationOptions,
    mutationFn: ({ removals, reason }) =>
      runInChunks({
        items: removals,
        chunkSize: REVOKE_ACCESS_CHUNK_SIZE,
        onProgress,
        // The reason describes the whole run, so every chunk carries it and
        // each removal is audited with it.
        send: (batch) => removeFundingAccess({ removals: batch, reason }),
        merge: mergeRemoveFundingResponses,
        deadChunk: (batch, reason) => ({
          ...EMPTY_REMOVE_FUNDING_RESPONSE,
          results: batch.map((item) => ({
            email: item.email,
            action: RemoveFundingAccessAction.Failed,
            invitationRevoked: false,
            rolesRemoved: false,
            profilesRemoved: 0,
            reason,
          })),
          failureCount: batch.length,
        }),
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      mutationOptions.onSuccess?.(data, variables, context)
    },
  })
}

const EMPTY_COMPANY_REVOKE_RESPONSE: RevokeCompanyAccessResponseDto = {
  results: [],
  removedCount: 0,
  skippedCount: 0,
  failureCount: 0,
}

const mergeCompanyRevokeResponses = (
  chunks: RevokeCompanyAccessResponseDto[]
): RevokeCompanyAccessResponseDto =>
  chunks.reduce(
    (merged, chunk) => ({
      results: [...merged.results, ...chunk.results],
      removedCount: merged.removedCount + chunk.removedCount,
      skippedCount: merged.skippedCount + chunk.skippedCount,
      failureCount: merged.failureCount + chunk.failureCount,
    }),
    EMPTY_COMPANY_REVOKE_RESPONSE
  )

/**
 * Removes one company from each recipient rather than their funding access as a
 * whole. Chunked like the other bulk access calls, for the same reason.
 */
export const useRevokeCompanyAccessMutation = (
  options?: BulkAccessMutationOptions<
    RevokeCompanyAccessResponseDto,
    RevokeCompanyAccessRequestDto
  >
) => {
  const queryClient = useQueryClient()
  const { onProgress, ...mutationOptions } = options ?? {}

  return useMutation<
    RevokeCompanyAccessResponseDto,
    Error,
    RevokeCompanyAccessRequestDto
  >({
    ...mutationOptions,
    mutationFn: ({ revocations }) =>
      runInChunks({
        items: revocations,
        chunkSize: REVOKE_ACCESS_CHUNK_SIZE,
        onProgress,
        send: (batch) => revokeCompanyAccess({ revocations: batch }),
        merge: mergeCompanyRevokeResponses,
        deadChunk: (batch, reason) => ({
          ...EMPTY_COMPANY_REVOKE_RESPONSE,
          results: batch.map((item) => ({
            email: item.email,
            action: RevokeCompanyAccessAction.Failed,
            success: false,
            rolesRemoved: false,
            reason,
          })),
          failureCount: batch.length,
        }),
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      mutationOptions.onSuccess?.(data, variables, context)
    },
  })
}
